import { useEffect, useMemo, useState } from 'react';

import { TAgent } from '@/types/agents';
import { TAgentConfig } from '@/types/configs';

import { SelectOption } from '../SelectWithNewOption/SelectWithNewOption';

interface UseCreateNewConfigModalProps {
  onClose: () => void;
  agent: TAgent;
  agentConfigs: TAgentConfig[];
  credentialOptions: Record<string, SelectOption[]>;
  onSubmit: (data: { configName: string; selectedCredentials: Record<string, string> }) => void;
}

const useCreateNewConfigModal = ({
  onClose,
  agent,
  agentConfigs,
  credentialOptions,
  onSubmit,
}: UseCreateNewConfigModalProps) => {
  const [configName, setConfigName] = useState('');
  const [selectedCredentials, setSelectedCredentials] = useState<Record<string, string>>({});
  const [localCredentialOptions, setLocalCredentialOptions] = useState<Record<string, SelectOption[]>>(credentialOptions);

  useEffect(() => {
    setLocalCredentialOptions(credentialOptions);
  }, [credentialOptions]);

  const handleCredentialChange = (credentialSlotKey: string, value: string) => {
    setSelectedCredentials((prev) => ({ ...prev, [credentialSlotKey]: value }));
  };

  const handleSubmit = () => {
    onSubmit({
      configName,
      selectedCredentials,
    });
    setConfigName('');
    setSelectedCredentials({});
  };

  const handleClose = () => {
    setConfigName('');
    setSelectedCredentials({});
    onClose();
  };

  const isDuplicateName = (name: string): boolean => {
    if (!name.trim() || !agentConfigs) {
      return false;
    }

    return agentConfigs.some(
      (config: TAgentConfig) => config.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
  };

  const getConfigNameError = (): string | undefined => {
    if (!configName.trim()) {
      return undefined;
    }
    if (isDuplicateName(configName)) {
      return 'Config with this name already exists. Config names must be unique to avoid confusion when assigning them to agents or chats.';
    }

    return undefined;
  };

  const isFormValid = useMemo(() => {
    if (!configName.trim()) {
      return false;
    }

    if (isDuplicateName(configName)) {
      return false;
    }

    return agent.credentials.every((_, slotIndex) => {
      const slotKey = `${agent.referenceId}:${slotIndex}`;
      const slotValue = selectedCredentials[slotKey];
      const hasSlotCredential = slotValue !== undefined && slotValue !== '';

      return hasSlotCredential;
    });
  }, [configName, selectedCredentials, agent.credentials, agentConfigs]);

  return {
    configName,
    setConfigName,
    selectedCredentials,
    localCredentialOptions,
    handleCredentialChange,
    handleSubmit,
    handleClose,
    getConfigNameError,
    isFormValid,
  };
};

export default useCreateNewConfigModal;